import { useEffect, useState } from 'react';
import { Plus, Pencil, Package } from 'lucide-react';
import { refaccionesApi } from '../api/endpoints';
import { Modal } from '../components/ui/Modal';
import { Spinner } from '../components/ui/Spinner';
import { SearchInput } from '../components/ui/SearchInput';
import { EmptyState } from '../components/ui/EmptyState';
import { Alert } from '../components/ui/Alert';
import { useAuthStore } from '../auth/authStore';

const empty = { codigo: '', nombre: '', descripcion: '', marca: '', modelo: '', costo: 0, stock: 0, unidad: 'Pieza' };

export function Refacciones() {
  const usuario = useAuthStore((s) => s.usuario);
  const isAdmin = usuario?.rol === 'Admin';
  const [data, setData] = useState(null);
  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const load = async () => {
    try { setData(await refaccionesApi.list()); }
    catch (e) { setError(e?.response?.data?.error || 'Error'); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(empty); setModalOpen(true); };
  const openEdit = (r) => { setEditing(r); setForm({ ...empty, ...r }); setModalOpen(true); };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, costo: Number(form.costo), stock: Number(form.stock) };
      if (editing) await refaccionesApi.update(editing.id, { ...payload, activo: editing.activo });
      else await refaccionesApi.create(payload);
      setModalOpen(false);
      await load();
    } catch (e) { setError(e?.response?.data?.error || 'Error al guardar'); }
    finally { setSaving(false); }
  };

  const filtered = (data ?? []).filter(r => !search || r.nombre.toLowerCase().includes(search.toLowerCase()) || r.codigo.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="p-3 sm:p-6 space-y-4">
      <div className="flex items-center justify-between gap-3">
        <SearchInput value={search} onChange={setSearch} placeholder="Buscar por nombre o código..." className="max-w-md" />
        {isAdmin && (
          <button onClick={openCreate} className="flex items-center gap-2 px-4 py-2 bg-cyan-500 hover:bg-cyan-600 text-white text-sm font-semibold rounded-lg shrink-0">
            <Plus size={16} /> <span className="hidden sm:inline">Nueva Refacción</span>
          </button>
        )}
      </div>

      {error && <Alert variant="error">{error}</Alert>}

      {data === null ? (
        <div className="flex justify-center py-12"><Spinner size={32} /></div>
      ) : filtered.length === 0 ? (
        <EmptyState icon={Package} title="Sin refacciones" description="No hay refacciones en el catálogo." />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.map((r) => (
            <div key={r.id} className="bg-panel border border-border rounded-xl p-4 hover:border-cyan-500/40 transition group">
              <div className="flex items-start justify-between mb-2">
                <div className="min-w-0 flex-1">
                  <span className="text-[10px] font-mono text-cyan-400 tracking-wider">{r.codigo}</span>
                  <h3 className="font-semibold text-white mt-0.5 truncate">{r.nombre}</h3>
                  {r.marca && <p className="text-[10px] text-muted truncate">{r.marca}{r.modelo && ` · ${r.modelo}`}</p>}
                </div>
                {isAdmin && (
                  <button onClick={() => openEdit(r)} className="p-1.5 rounded text-slate-400 hover:text-cyan-400 hover:bg-cyan-500/10 shrink-0 ml-2 md:opacity-0 md:group-hover:opacity-100" title="Editar">
                    <Pencil size={14} />
                  </button>
                )}
              </div>
              {r.descripcion && <p className="text-xs text-muted line-clamp-2 mb-3">{r.descripcion}</p>}
              <div className="flex items-center justify-between pt-2 border-t border-border">
                <span className="text-lg font-bold text-emerald-400">${Number(r.costo).toLocaleString('es-MX', { minimumFractionDigits: 2 })}</span>
                <div className="flex items-center gap-2">
                  <span className={`inline-block px-2 py-0.5 rounded text-xs font-bold ${r.stock > 5 ? 'text-emerald-400 bg-emerald-500/10' : r.stock > 0 ? 'text-amber-400 bg-amber-500/10' : 'text-rose-400 bg-rose-500/10'}`}>STOCK: {r.stock}</span>
                  <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold ${r.activo ? 'bg-emerald-500/15 text-emerald-400' : 'bg-rose-500/15 text-rose-400'}`}>{r.activo ? 'ACTIVO' : 'INACTIVO'}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editing ? 'Editar Refacción' : 'Nueva Refacción'} size="md">
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Código *" value={form.codigo} onChange={(v) => setForm({ ...form, codigo: v })} />
            <Field label="Unidad" value={form.unidad} onChange={(v) => setForm({ ...form, unidad: v })} />
          </div>
          <Field label="Nombre *" value={form.nombre} onChange={(v) => setForm({ ...form, nombre: v })} />
          <Field label="Descripción" value={form.descripcion} onChange={(v) => setForm({ ...form, descripcion: v })} />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Marca" value={form.marca} onChange={(v) => setForm({ ...form, marca: v })} />
            <Field label="Modelo" value={form.modelo} onChange={(v) => setForm({ ...form, modelo: v })} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Costo *" type="number" value={form.costo} onChange={(v) => setForm({ ...form, costo: v })} />
            <Field label="Stock" type="number" value={form.stock} onChange={(v) => setForm({ ...form, stock: v })} />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-5 pt-4 border-t border-border">
          <button onClick={() => setModalOpen(false)} className="px-4 py-2 rounded border border-border text-slate-300 hover:bg-panel-2 text-sm">Cancelar</button>
          <button onClick={save} disabled={saving || !form.codigo || !form.nombre} className="px-4 py-2 rounded bg-cyan-500 hover:bg-cyan-600 text-white text-sm font-semibold disabled:opacity-50">
            {saving ? 'Guardando...' : (editing ? 'Actualizar' : 'Crear')}
          </button>
        </div>
      </Modal>
    </div>
  );
}

function Field({ label, value, onChange, type = 'text' }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-300 mb-1.5 tracking-wide">{label}</label>
      <input type={type} value={value || ''} onChange={(e) => onChange(e.target.value)} className="w-full px-3 py-2 bg-panel-2 border border-border rounded-md text-sm text-white focus:outline-none focus:border-cyan-500" />
    </div>
  );
}
